/**
 * 転送・エイリアス（05-ui.md §8.5）。
 *
 * 契約者には「エイリアス」と「転送」の違いが分かりにくいので、
 * タブを分けたうえで冒頭に必ず違いの説明を置く。
 *   エイリアス = 受け取るためのアドレスを増やす（メールボックスは増えない）
 *   転送       = 届いたメールを別のアドレスへ送る
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CursorPagination,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useCursorPagination,
  type DataTableColumn,
} from '@mail-sv/ui';
import { Forward, PauseCircle, Pencil, PlayCircle, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains, useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { PlanLimitAlert } from '../components/plan-limit.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import type { Alias, CursorPage, Forwarder, Mailbox } from '../lib/api-types.js';

export function AliasesPage() {
  return (
    <>
      <PageHeader
        title="転送・エイリアス"
        description="別名のメールアドレスを作ったり、届いたメールを他のアドレスへ転送したりできます。"
      />

      <div className="space-y-5">
        <HelpNote title="エイリアスと転送のちがい">
          <ul className="list-disc space-y-1 pl-4">
            <li>
              <strong>エイリアス</strong>：受け取るためのアドレスを増やします。例えば
              <code> sales@example.jp</code> 宛のメールを <code>info@example.jp</code>{' '}
              で受け取れます。メールアドレスの数（プランの上限）は消費しません。
            </li>
            <li>
              <strong>転送</strong>：このサービスで受け取ったメールを、別のメールアドレス
              （社外のアドレスなど）へ自動で送ります。
            </li>
          </ul>
        </HelpNote>

        <Tabs defaultValue="aliases">
          <TabsList>
            <TabsTrigger value="aliases">エイリアス</TabsTrigger>
            <TabsTrigger value="forwarders">外部への転送</TabsTrigger>
          </TabsList>
          <TabsContent value="aliases">
            <AliasesTab />
          </TabsContent>
          <TabsContent value="forwarders">
            <ForwardersTab />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- エイリアス */

function AliasesTab() {
  const org = useOrg();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Alias | null>(null);
  const page = useCursorPagination(50);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: qk.aliases({ cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<Alias>>(`/aliases?${params.toString()}`);
    },
  });

  const remove = useMutation({
    // 削除も 202 + jobId（ドメインの desired-state 再生成が走る）。
    mutationFn: (id: string) =>
      api.del<{ jobId: string | null }>(`/aliases/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.aliases() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
    },
  });

  /*
   * **一時停止（`enabled`）と設定変更。** 以前はエイリアスを変える手段が
   * 「削除して作り直す」しか無く、受け取りを一時的に止めたいだけの場合でも
   * アドレスが消えてしまっていた（その間に届いたメールは失われる）。
   * `PATCH /aliases/:aliasId` は 202 + `{jobId}` で、反映はノードへの
   * desired-state 配布を待つ。
   */
  const patch = useMutation({
    mutationFn: (args: { id: string; enabled: boolean }) =>
      api.patch<{ jobId: string | null }>(`/aliases/${encodeURIComponent(args.id)}`, {
        enabled: args.enabled,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.aliases() });
    },
  });

  const canWrite = canManageOrg(user);

  const columns: Array<DataTableColumn<Alias>> = [
    {
      id: 'address',
      header: 'エイリアス（受け取るアドレス）',
      cell: (row) => (
        <div className="min-w-0">
          <span className="font-medium">{row.address}</span>
          {!row.enabled ? (
            <Badge variant="warning" size="sm" className="ml-2">
              停止中
            </Badge>
          ) : null}
          {row.description ? (
            <p className="truncate text-xs text-muted-foreground">{row.description}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'destinations',
      header: '実際に届く先',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.destinations.map((d) => (
            <Badge key={d} variant="muted" size="sm">
              {d}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'kind',
      header: '種別',
      width: 'w-32',
      hideBelow: 'md',
      cell: (row) => (
        <Badge variant="secondary" size="sm">
          {row.destinations.length > 1 ? '複数へ配送' : 'エイリアス'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-56',
      align: 'right',
      cell: (row) =>
        canWrite ? (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
              <Pencil aria-hidden />
              変更
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={patch.isPending && patch.variables?.id === row.id}
              onClick={() => patch.mutate({ id: row.id, enabled: !row.enabled })}
            >
              {row.enabled ? (
                <>
                  <PauseCircle aria-hidden />
                  停止
                </>
              ) : (
                <>
                  <PlayCircle aria-hidden />
                  再開
                </>
              )}
            </Button>
            <ConfirmDialogButton
              title={`${row.address} を削除します`}
              description="このエイリアス宛のメールは受け取れなくなります。一時的に止めたいだけなら「停止」をお使いください。"
              confirmLabel="削除する"
              pending={remove.isPending}
              onConfirm={() => remove.mutate(row.id)}
            >
              <Trash2 aria-hidden />
              <span className="sr-only">削除</span>
            </ConfirmDialogButton>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <PlanLimitAlert
        resourceLabel="エイリアス"
        current={org.data?.usage.aliases ?? 0}
        max={org.data?.plan.limits.aliasesMax ?? 0}
        canUpgrade={canManageOrg(user)}
      />

      {canWrite ? (
        <div className="flex justify-end">
          <Button onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden />
            エイリアスを追加
          </Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.isError ? 'エイリアスを取得できませんでした。' : undefined}
        empty={
          <EmptyState
            icon={Forward}
            title="エイリアスはまだありません"
            description="sales@ や contact@ など、受け取り用のアドレスを増やしたいときに使います。メールアドレスの数を消費しません。"
            action={
              canWrite ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus aria-hidden />
                  エイリアスを追加
                </Button>
              ) : null
            }
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.data?.nextCursor ?? null}
            canGoPrev={page.canGoPrev}
            onNext={() => page.goNext(list.data?.nextCursor ?? null)}
            onPrev={page.goPrev}
            limit={page.limit}
            onLimitChange={page.setLimit}
            currentCount={list.data?.items.length}
            loading={list.isFetching}
          />
        }
      />

      {patch.isError ? <FormAlert error={patch.error} /> : null}

      <CreateAliasDialog open={createOpen} onOpenChange={setCreateOpen} />
      {editing ? <EditAliasDialog alias={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

/**
 * エイリアスの変更（`PATCH /aliases/:aliasId`）。
 *
 * 変えられるのは**届け先・メモ・有効/無効**の 3 つだけで、アドレスそのものは
 * 変えられない（アドレスを変えるのは別のエイリアスを作るのと同じで、
 * 旧アドレス宛が黙って届かなくなるのを避けるため API にも口が無い）。
 *
 * 届け先は**同じドメインのメールボックス**に限られる（サーバーが 400 で断る）。
 * 外部アドレスへ出したいときは「外部への転送」タブ側の機能で、
 * あちらは受け取り側の同意確認が要る。
 */
function EditAliasDialog({ alias, onClose }: { alias: Alias; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [destinationIds, setDestinationIds] = React.useState<string[]>(alias.destinationMailboxIds);
  const [description, setDescription] = React.useState(alias.description ?? '');

  const mailboxes = useQuery({
    queryKey: qk.mailboxes({ domainId: alias.domainId, forAlias: true }),
    queryFn: () =>
      api.get<CursorPage<Mailbox>>(
        `/mailboxes?domainId=${encodeURIComponent(alias.domainId)}&limit=200`,
      ),
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ jobId: string | null }>(`/aliases/${encodeURIComponent(alias.id)}`, {
        destinationMailboxIds: destinationIds,
        description: description.trim() === '' ? null : description.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.aliases() });
      onClose();
    },
  });

  const toggle = (id: string) => {
    setDestinationIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <Dialog open onOpenChange={(next) => (save.isPending ? undefined : !next && onClose())}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (destinationIds.length > 0) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{alias.address} の設定</DialogTitle>
            <DialogDescription>
              届け先とメモを変更できます。アドレスそのものは変更できません。
            </DialogDescription>
          </DialogHeader>

          <FormAlert error={save.error} />

          <Field label="届け先" required>
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {(mailboxes.data?.items ?? []).map((m) => (
                <label key={m.id} className="flex cursor-pointer items-center gap-2 p-1 text-sm">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={destinationIds.includes(m.id)}
                    onChange={() => toggle(m.id)}
                  />
                  <span className="truncate">{m.address}</span>
                </label>
              ))}
              {mailboxes.isPending ? (
                <p className="p-1 text-xs text-muted-foreground">読み込み中…</p>
              ) : null}
            </div>
          </Field>

          {destinationIds.length === 0 ? (
            <p className="text-xs text-destructive">届け先を 1 つ以上選んでください。</p>
          ) : null}

          <Field label="メモ（任意）" hint="用途を書いておくと、あとで見返したときに分かります。">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              placeholder="例: 問い合わせフォーム用"
            />
          </Field>

          <SubmitBar
            submitLabel="保存する"
            pending={save.isPending}
            disabled={destinationIds.length === 0}
            onCancel={onClose}
            hint="保存後、サーバーへ反映されるまで少し時間がかかります。"
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateAliasDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const domains = useDomains();
  const queryClient = useQueryClient();
  const [localPart, setLocalPart] = React.useState('');
  const [domainId, setDomainId] = React.useState('');
  const [destinationIds, setDestinationIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setLocalPart('');
    setDestinationIds([]);
    setDomainId(domains.data?.items[0]?.id ?? '');
    // 開いた瞬間の値で初期化する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ドメインを変えたら、別ドメインのメールボックスを選んだままにしない。
  React.useEffect(() => {
    setDestinationIds([]);
  }, [domainId]);

  /**
   * 配送先は **同一ドメインのメールボックス ID** で指定する（API の
   * `destinationMailboxIds`）。以前はアドレスを自由入力させていたが、
   * API はアドレスを受け付けないので必ず 400 になっていた。
   * 自由入力を残すと「サービス外のアドレスも書ける」ように見えてしまうのも問題で、
   * 外部への配送は「外部への転送」タブの機能（同意確認が要る）である。
   */
  const mailboxes = useQuery({
    queryKey: qk.mailboxes({ domainId, forAlias: true }),
    queryFn: () =>
      api.get<CursorPage<Mailbox>>(`/mailboxes?domainId=${encodeURIComponent(domainId)}&limit=200`),
    enabled: open && domainId !== '',
  });

  const create = useMutation({
    // 202 + `{jobId, aliasId}`。
    mutationFn: () =>
      api.post<{ jobId: string; aliasId: string }>('/aliases', {
        localPart,
        domainId,
        destinationMailboxIds: destinationIds,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.aliases() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
      onOpenChange(false);
    },
  });

  const toggleDestination = (id: string) => {
    setDestinationIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const localOk = /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/.test(localPart);
  const destOk = destinationIds.length > 0;

  return (
    <Dialog open={open} onOpenChange={(n) => (create.isPending ? undefined : onOpenChange(n))}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (localOk && destOk) create.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>エイリアスを追加</DialogTitle>
            <DialogDescription>
              受け取り用の別名アドレスを作ります。届いたメールは指定した宛先に配送されます。
            </DialogDescription>
          </DialogHeader>

          <FormAlert error={create.error} />

          <div className="flex flex-wrap items-end gap-2">
            <Field
              label="エイリアス（@ より前）"
              htmlFor="alias-local"
              required
              className="min-w-[10rem] flex-1"
              error={localPart.length > 0 && !localOk ? '使えない文字が含まれています' : undefined}
            >
              <Input
                id="alias-local"
                value={localPart}
                onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
                placeholder="sales"
                autoFocus
              />
            </Field>
            <span className="pb-3 text-lg text-muted-foreground">@</span>
            <Field label="ドメイン" className="min-w-[10rem] flex-1">
              <Select value={domainId} onValueChange={setDomainId}>
                <SelectTrigger>
                  <SelectValue placeholder="ドメインを選択" />
                </SelectTrigger>
                <SelectContent>
                  {(domains.data?.items ?? []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field
            label="届ける先"
            required
            hint="同じドメインのメールアドレスから選びます。社外のアドレスへ送りたい場合は「外部への転送」を使います。"
          >
            {mailboxes.isPending ? (
              <p className="text-sm text-muted-foreground">読み込んでいます…</p>
            ) : (mailboxes.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                このドメインにはまだメールアドレスがありません。先に作成してください。
              </p>
            ) : (
              <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-border p-3">
                {(mailboxes.data?.items ?? []).map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={destinationIds.includes(m.id)}
                      onChange={() => toggleDestination(m.id)}
                    />
                    <span className="truncate">{m.address}</span>
                  </label>
                ))}
              </div>
            )}
          </Field>

          <SubmitBar
            submitLabel="追加する"
            pending={create.isPending}
            disabled={!localOk || !destOk || !domainId}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ 転送 */

function ForwardersTab() {
  const page = useCursorPagination(50);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canWrite = canManageOrg(user);

  const list = useQuery({
    queryKey: qk.forwarders({ cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<Forwarder>>(`/forwarders?${params.toString()}`);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.del<{ jobId: string | null }>(`/forwarders/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.forwarders() }),
  });

  const columns: Array<DataTableColumn<Forwarder>> = [
    {
      id: 'mailbox',
      header: '転送元',
      cell: (row) => (
        <AppLink to={`/mailboxes/${row.mailboxId}`} className="font-medium hover:underline">
          {row.mailboxAddress}
        </AppLink>
      ),
    },
    { id: 'destination', header: '転送先', cell: (row) => row.destination },
    {
      id: 'keepCopy',
      header: '元にも残す',
      width: 'w-28',
      hideBelow: 'md',
      cell: (row) => (
        <Badge variant={row.keepCopy ? 'success' : 'muted'} size="sm">
          {row.keepCopy ? '残す' : '残さない'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-24',
      align: 'right',
      cell: (row) =>
        canWrite ? (
          <ConfirmDialogButton
            title="転送設定を削除します"
            description={`${row.mailboxAddress} から ${row.destination} への転送を停止します。`}
            confirmLabel="削除する"
            pending={remove.isPending}
            onConfirm={() => remove.mutate(row.id)}
          >
            <Trash2 aria-hidden />
            削除
          </ConfirmDialogButton>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">転送の追加はメールアドレスごとに行います</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            転送を追加するには、対象のメールアドレスを開き「転送」タブから設定してください。
          </p>
          <Button asChild variant="outline" size="sm" className="mt-3">
            <AppLink to="/mailboxes">メールアドレス一覧へ</AppLink>
          </Button>
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.isError ? '転送設定を取得できませんでした。' : undefined}
        emptyTitle="転送設定はありません"
        emptyDescription="メールアドレスの詳細画面から、転送先を追加できます。"
        footer={
          <CursorPagination
            nextCursor={list.data?.nextCursor ?? null}
            canGoPrev={page.canGoPrev}
            onNext={() => page.goNext(list.data?.nextCursor ?? null)}
            onPrev={page.goPrev}
            limit={page.limit}
            onLimitChange={page.setLimit}
            currentCount={list.data?.items.length}
            loading={list.isFetching}
          />
        }
      />
    </div>
  );
}
